import type { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query'
import { createApi } from '@reduxjs/toolkit/query/react'

import { HTTP_METHODS } from 'common/constants/httpMethodsConstants'
import { baseQuery } from 'common/store/api/fetchBaseQuery'
import { REDUCERS } from 'common/store/reducerConstants'

import { PATIENT_STATUSES } from 'features/Home/constants/infoConstants'
import type { IPatientRoom, IPatientRoomWithTimeSlots } from 'features/Home/interfaces/IInfoPatient'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import type { IPatientSelection } from 'features/Patients/interfaces/IPatient'
import {
  applyPatientDelete,
  applyPatientStatusUpdate,
  applyPatientUpsert,
  applyRoomStatusUpdate,
  mergeScheduleQueryData,
  patchAppointmentOptimistic,
} from 'features/Schedule/api/optimistics.updates'
import {
  SCHEDULE_CLIENT_ID,
  SCHEDULE_CLIENT_ID_HEADER,
} from 'features/Schedule/pusher/scheduleClientId'
import {
  markScheduleMutationPending,
  settleScheduleMutationPending,
  toScheduleSlotSnapshot,
} from 'features/Schedule/pusher/schedulePusherEcho'
import { type PatientAppointmentType } from 'features/Schedule/pusher/type'
import type {
  AvailableSlots,
  ScheduleAppointmentDto,
  ScheduleDay,
  ScheduleDayResponse,
  ScheduleMonth,
  ScheduleWeek,
} from './schedule.types'

export enum SCHEDULE_TAGS {
  SCHEDULE = 'Schedule',
  SCHEDULE_WEEK = 'ScheduleWeek',
}

const mutationHeaders = (siteId: string) => ({
  'Database-Connection': siteId,
  [SCHEDULE_CLIENT_ID_HEADER]: SCHEDULE_CLIENT_ID,
})

const findCachedAppointment = (
  getState: () => unknown,
  siteId: string,
  appointmentId: number,
): IPatientSchedule | undefined => {
  const state = getState() as {
    schedule?: {
      queries?: Record<
        string,
        { data?: { items?: IPatientSchedule[] }; originalArgs?: { siteId?: string } }
      >
    }
  }
  const queries = state.schedule?.queries
  if (!queries) return undefined

  for (const query of Object.values(queries)) {
    if (query?.originalArgs?.siteId !== siteId) continue
    const found = query.data?.items?.find((item) => Number(item.id) === Number(appointmentId))
    if (found) return found
  }

  return undefined
}

// Per-appointment sequence gate for update mutations.
// Only the latest started update is allowed to reconcile/rollback cache state.
let scheduleUpdateSeq = 0
const latestScheduleUpdateSeqByAppointment = new Map<number, number>()

const beginScheduleUpdateMutation = (appointmentId: number): number => {
  const seq = ++scheduleUpdateSeq
  latestScheduleUpdateSeqByAppointment.set(appointmentId, seq)
  return seq
}

const isLatestScheduleUpdateMutation = (appointmentId: number, seq: number): boolean =>
  latestScheduleUpdateSeqByAppointment.get(appointmentId) === seq

export const scheduleApi = createApi({
  reducerPath: REDUCERS.SCHEDULE,
  baseQuery: baseQuery(),
  tagTypes: Object.values(SCHEDULE_TAGS),
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({
    fetchScheduleMonth: build.query<
      ScheduleMonth,
      { siteId: string; startDate: string; endDate: string }
    >({
      query: ({ siteId, startDate, endDate }) => ({
        url: `/schedule/month/${startDate}/${endDate}`,
        headers: { 'Database-Connection': siteId },
      }),
      providesTags: [SCHEDULE_TAGS.SCHEDULE],
    }),

    fetchScheduleWeek: build.query<
      ScheduleWeek,
      { siteId: string; startDate: string; endDate: string }
    >({
      query: ({ siteId, startDate, endDate }) => ({
        url: `/schedule/week/${startDate}/${endDate}`,
        headers: { 'Database-Connection': siteId },
      }),
      transformResponse: (
        response: Omit<ScheduleWeek, 'rooms'> & { rooms: IPatientRoomWithTimeSlots[] },
      ): ScheduleWeek => {
        const rooms = getAdjustedRooms(response.rooms)
        return {
          ...response,
          rooms,
          items: response.items.map((item) => ({
            ...item,
            room: rooms.find((room) => {
              return String(room.value) === String(item.room.code)
            }),
          })) as IPatientSchedule[],
        }
      },
      merge: (currentCache, incoming) => {
        mergeScheduleQueryData(currentCache, incoming)
      },
      providesTags: [SCHEDULE_TAGS.SCHEDULE_WEEK],
    }),

    fetchScheduleDay: build.query<ScheduleDay, { siteId: string; date: string }>({
      query: ({ siteId, date }) => ({
        url: `/schedule/day/${date}`,
        headers: { 'Database-Connection': siteId },
      }),
      transformResponse: (response: ScheduleDayResponse): ScheduleDay => {
        const rooms = getAdjustedRooms(response.rooms)
        return {
          ...response,
          rooms,
          items: response.items.map((item) => ({
            ...item,
            room: rooms.find((room) => {
              return String(room.value) === String(item.room.code)
            }),
          })) as IPatientSchedule[],
        }
      },
      merge: (currentCache, incoming) => {
        mergeScheduleQueryData(currentCache, incoming)
      },
      providesTags: [SCHEDULE_TAGS.SCHEDULE],
    }),

    createScheduleAppointment: build.mutation<
      PatientAppointmentType,
      { siteId: string; date: string; data: ScheduleAppointmentDto }
    >({
      query: ({ siteId, date, data }) => ({
        url: `/schedule/day/${date}/appointment`,
        method: HTTP_METHODS.POST,
        headers: mutationHeaders(siteId),
        data,
      }),
      async onQueryStarted({ siteId, date }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: responseData } = await queryFulfilled
          const state = getState()
          applyPatientUpsert(
            dispatch,
            {
              siteId,
              viewDate: date,
              scheduleQueries: state.schedule.queries,
            },
            responseData,
          )
        } catch {
          console.log('Something went wrong with createScheduleAppointment')
        }
      },
    }),

    updateScheduleAppointment: build.mutation<
      PatientAppointmentType,
      { siteId: string; date: string; appointmentId: number; data: ScheduleAppointmentDto }
    >({
      query: ({ siteId, date, appointmentId, data }) => ({
        url: `/schedule/day/${date}/appointment/${appointmentId}`,
        method: HTTP_METHODS.PUT,
        headers: mutationHeaders(siteId),
        data,
      }),
      async onQueryStarted(
        { siteId, date, appointmentId, data },
        { dispatch, queryFulfilled, getState },
      ) {
        const mutationSeq = beginScheduleUpdateMutation(appointmentId)
        const existing = findCachedAppointment(getState, siteId, appointmentId)
        markScheduleMutationPending(appointmentId, {
          kind: 'upsert',
          previous: existing ? toScheduleSlotSnapshot(existing) : undefined,
          intended: toScheduleSlotSnapshot({
            date,
            start_time: data.start_time ?? existing?.start_time,
            end_time: data.end_time ?? existing?.end_time,
            room: data.room ?? existing?.room,
          }),
        })
        const optimisticPatches = patchAppointmentOptimistic(
          dispatch,
          siteId,
          date,
          appointmentId,
          data,
          getState,
        )

        try {
          const { data: responseData } = await queryFulfilled
          settleScheduleMutationPending(appointmentId)
          if (!isLatestScheduleUpdateMutation(appointmentId, mutationSeq)) return
          const state = getState()
          applyPatientUpsert(
            dispatch,
            {
              siteId,
              viewDate: date,
              scheduleQueries: state.schedule.queries,
            },
            responseData,
            { allowStructural: false },
          )
        } catch {
          settleScheduleMutationPending(appointmentId)
          if (!isLatestScheduleUpdateMutation(appointmentId, mutationSeq)) return
          optimisticPatches.forEach((patch) => patch.undo())
          console.log('Something went wrong with updateScheduleAppointment')
        }
      },
    }),

    notifyMultiplePatient: build.mutation<void, { siteId: string; appointmentIds: number[] }>({
      query: ({ siteId, appointmentIds }) => ({
        url: `/schedule/send-notification`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
        data: { appointment_ids: appointmentIds },
      }),
    }),

    deleteScheduleAppointment: build.mutation<
      void,
      { siteId: string; date: string; appointmentId: number; sendSms?: number }
    >({
      query: ({ siteId, date, appointmentId, sendSms = 0 }) => ({
        url: `/schedule/day/${date}/appointment/${appointmentId}/${sendSms}`,
        method: HTTP_METHODS.DELETE,
        headers: mutationHeaders(siteId),
      }),
      async onQueryStarted(
        { siteId, date, appointmentId },
        { dispatch, queryFulfilled, getState },
      ) {
        markScheduleMutationPending(appointmentId, { kind: 'delete' })
        const optimisticPatches = applyPatientDelete(
          dispatch,
          {
            siteId,
            viewDate: date,
            scheduleQueries: getState().schedule.queries,
          },
          appointmentId,
        )
        try {
          await queryFulfilled
          settleScheduleMutationPending(appointmentId)
        } catch {
          settleScheduleMutationPending(appointmentId)
          optimisticPatches.forEach((patch) => patch.undo())
          console.log('Something went wrong with deleteScheduleAppointment')
        }
      },
    }),

    checkInPatient: build.mutation<
      { temperature: number },
      { siteId: string; date: string; appointmentId: number; access_code: number | string }
    >({
      query: ({ siteId, appointmentId, access_code }) => ({
        url: `/schedule/check-in-patient/${appointmentId}`,
        headers: mutationHeaders(siteId),
        method: HTTP_METHODS.POST,
        data: { access_code },
      }),
      async onQueryStarted(
        { siteId, date, appointmentId },
        { dispatch, queryFulfilled, getState },
      ) {
        markScheduleMutationPending(appointmentId, { kind: 'status' })
        try {
          const { data: responseData } = await queryFulfilled
          settleScheduleMutationPending(appointmentId)
          const state = getState()
          applyPatientStatusUpdate(
            dispatch,
            {
              siteId,
              viewDate: date,
              scheduleQueries: state.schedule.queries,
            },
            appointmentId,
            PATIENT_STATUSES.CHECKED_IN_ON_SITE,
            responseData.temperature,
          )
        } catch {
          settleScheduleMutationPending(appointmentId)
          console.log('Something went wrong with checkInPatient')
        }
      },
    }),

    notifyPatient: build.mutation<void, { siteId: string; appointmentId: number }>({
      query: ({ siteId, appointmentId }) => ({
        url: `/schedule/notify-patient/${appointmentId}`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
      }),
    }),

    updateRoomStatus: build.mutation<
      {
        room_status: {
          code: number
          name: string
        }
        patients: IPatientSelection[]
      },
      {
        siteId: string
        date: string
        roomId: number
        data: {
          status_type_code: number
          lease_id: string
          force_update?: number
        }
      }
    >({
      query: ({ siteId, roomId, data }) => ({
        data,
        url: `/room/${roomId}/status`,
        method: HTTP_METHODS.PUT,
        headers: mutationHeaders(siteId),
      }),
      async onQueryStarted({ siteId, date, roomId }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: responseData } = await queryFulfilled
          const state = getState()
          applyRoomStatusUpdate(
            dispatch,
            {
              siteId,
              viewDate: date,
              scheduleQueries: state.schedule.queries,
            },
            roomId,
            responseData.room_status.code,
          )
        } catch {
          console.log('Something went wrong with updateRoomStatus')
        }
      },
    }),
    updateSelectPatients: build.mutation<
      { data: { status: boolean; message: string } },
      { siteId: string; roomId: number; data: { appointment_ids: number[] } }
    >({
      query: ({ siteId, roomId, data }) => ({
        data,
        url: `/room/${roomId}/appointments`,
        method: HTTP_METHODS.POST,
        headers: { 'Database-Connection': siteId },
      }),
    }),

    findAvailableSlots: build.query<
      AvailableSlots,
      { siteId: string; date: string; for_days: number; duration: number; room_type: number }
    >({
      query: ({ siteId, date, for_days, duration, room_type }) => ({
        url: `/schedule/available-slots`,
        method: HTTP_METHODS.GET,
        headers: { 'Database-Connection': siteId },
        params: { date, for_days, duration, room_type },
      }),
    }),
  }),
})

export const {
  useFetchScheduleMonthQuery,
  useFetchScheduleWeekQuery,
  useFetchScheduleDayQuery,
  useCreateScheduleAppointmentMutation,
  useUpdateScheduleAppointmentMutation,
  useNotifyPatientMutation,
  useNotifyMultiplePatientMutation,
  useDeleteScheduleAppointmentMutation,
  useCheckInPatientMutation,
  useUpdateRoomStatusMutation,
  useUpdateSelectPatientsMutation,
  useLazyFindAvailableSlotsQuery,
} = scheduleApi

const getAdjustedRooms = (rooms: IPatientRoomWithTimeSlots[]): IPatientRoom[] => {
  return rooms.map((room) => ({
    ...room,
    slots: room.time_slots,
    value: String(room.code),
    label: room.name,
    is_waiting_room: !!room.is_waiting_room,
  }))
}
